/**
 * Missile Defence, as a pure state machine.
 *
 * Unlike Sheep Race, nothing here has to be kept from the player: the game is
 * played alone and nothing is stored (migrations/0060_missile_defence_game.sql),
 * so the rules can live in the browser. They live HERE rather than in the page
 * so they can be asserted without a canvas — `scripts/missile-game-check.mjs`
 * drives every rule below through `step()` with a seeded random source.
 *
 * Every function returns a new state and never mutates its argument. The page
 * holds the state in a ref and redraws it each animation frame.
 */

/** The playfield, in world units. The page scales it to fit its container. */
export const WIDTH = 800
export const HEIGHT = 500
/** Where a missile counts as having landed. The turret sits on this line. */
export const GROUND_Y = HEIGHT - 24
export const TURRET_X = WIDTH / 2
export const TURRET_Y = GROUND_Y
/** Length of the barrel; a shot leaves from its tip. */
export const BARREL = 34

/** Missiles destroyed to win the round. */
export const TARGET = 20

/** Speed of the player's shot, units per second. */
export const SHOT_SPEED = 620
/**
 * Seconds between shots. Holding the spacebar would otherwise fire a wall of
 * shots on key-repeat and make aiming pointless.
 */
export const FIRE_COOLDOWN = 0.22
/** A shot this close to a missile's head destroys it. */
export const HIT_RADIUS = 16

/**
 * The barrel cannot point below this angle from the horizontal, either side.
 * A turret on the ground that could aim at the ground would only waste shots.
 */
export const MIN_AIM = (10 * Math.PI) / 180

/**
 * The longest step `step()` integrates in one go. A frame is split into slices
 * no longer than this, so a shot cannot pass straight through a missile on a
 * slow frame — at 620 units/s this is about 5 units per slice, well inside
 * HIT_RADIUS.
 */
export const MAX_SLICE = 1 / 120
/**
 * A frame longer than this is treated as this long. A tab in the background
 * stops animation frames entirely; without the cap, coming back to it would
 * integrate the whole absence in one step and land every missile at once.
 */
export const MAX_FRAME = 0.1

export type Status = 'ready' | 'playing' | 'won' | 'lost'

export interface Shot {
  id: number
  x: number
  y: number
  vx: number
  vy: number
}

export interface Missile {
  id: number
  x: number
  y: number
  vx: number
  vy: number
}

export interface GameState {
  status: Status
  /** Barrel angle in radians: 0 points right, PI/2 straight up, PI left. */
  aim: number
  shots: Shot[]
  missiles: Missile[]
  destroyed: number
  /** Seconds since the last missile was launched. */
  sinceSpawn: number
  /** Seconds since the last shot. */
  sinceShot: number
  /** Where the missile that ended the round came down, for the page to draw. */
  impactX: number | null
  nextId: number
}

/** A number in [0, 1). The page passes Math.random; the check passes a seed. */
export type Rng = () => number

export interface Difficulty {
  /** Units per second a missile falls. */
  fallSpeed: number
  /** Seconds between launches, when there is room for another. */
  spawnEvery: number
  /** The most missiles in the air at once. */
  maxAirborne: number
}

/**
 * The ramp, by progress rather than by the clock: how many you have shot down
 * so far decides how hard the next one is. A player who is struggling is
 * therefore not pushed harder for struggling — the game gets harder only as
 * fast as they get closer to winning.
 *
 * One missile at a time, falling over about seven seconds, to start; by the
 * last few, three at once falling in under three. Each of the three dials moves
 * linearly from the first value to the last; `maxAirborne` steps up at a
 * third and two thirds of the way.
 */
export function difficulty(destroyed: number): Difficulty {
  const t = Math.min(1, Math.max(0, destroyed / (TARGET - 1)))
  return {
    fallSpeed: 65 + (170 - 65) * t,
    spawnEvery: 2.4 + (0.8 - 2.4) * t,
    maxAirborne: 1 + Math.floor(t * 2.999),
  }
}

/** A fresh round, waiting to be started. */
export function newGame(): GameState {
  return {
    status: 'ready',
    aim: Math.PI / 2,
    shots: [],
    missiles: [],
    destroyed: 0,
    // The first missile comes a second after Start, not a whole interval.
    sinceSpawn: difficulty(0).spawnEvery - 1,
    sinceShot: FIRE_COOLDOWN,
    impactX: null,
    nextId: 1,
  }
}

/** Start (or restart) a round. */
export function start(state: GameState): GameState {
  return { ...newGame(), aim: state.aim, status: 'playing' }
}

/**
 * Point the barrel at a spot on the playfield, clamped so it never points
 * below MIN_AIM. A pointer below the turret — off the bottom of the canvas,
 * say — aims flat to that side rather than flipping the barrel round.
 */
export function aimAt(state: GameState, x: number, y: number): GameState {
  const dx = x - TURRET_X
  const dy = TURRET_Y - y
  let angle = Math.atan2(dy, dx)
  // atan2 answers in (-PI, PI]; below the horizon is negative. Send it to the
  // nearer side's limit.
  if (angle < MIN_AIM || angle > Math.PI - MIN_AIM) {
    angle = dx >= 0 ? MIN_AIM : Math.PI - MIN_AIM
  }
  return { ...state, aim: angle }
}

/** Where the barrel's tip is, which is where a shot appears. */
export function muzzle(aim: number): { x: number; y: number } {
  return { x: TURRET_X + Math.cos(aim) * BARREL, y: TURRET_Y - Math.sin(aim) * BARREL }
}

/** Fire along the barrel, if a round is on and the cooldown has passed. */
export function fire(state: GameState): GameState {
  if (state.status !== 'playing' || state.sinceShot < FIRE_COOLDOWN) return state
  const tip = muzzle(state.aim)
  const shot: Shot = {
    id: state.nextId,
    x: tip.x,
    y: tip.y,
    vx: Math.cos(state.aim) * SHOT_SPEED,
    vy: -Math.sin(state.aim) * SHOT_SPEED,
  }
  return { ...state, shots: [...state.shots, shot], sinceShot: 0, nextId: state.nextId + 1 }
}

/**
 * Launch one missile from a random spot along the top, aimed at a random spot
 * on the ground. A diagonal is what makes leading the target a skill; a
 * straight drop would be a game of lining up x.
 */
function launch(state: GameState, rng: Rng): GameState {
  const { fallSpeed } = difficulty(state.destroyed)
  const margin = 30
  const x = margin + rng() * (WIDTH - 2 * margin)
  const landAt = margin + rng() * (WIDTH - 2 * margin)
  const seconds = GROUND_Y / fallSpeed
  const missile: Missile = { id: state.nextId, x, y: 0, vx: (landAt - x) / seconds, vy: fallSpeed }
  return { ...state, missiles: [...state.missiles, missile], sinceSpawn: 0, nextId: state.nextId + 1 }
}

/** One slice: move, collide, land, launch. `dt` is at most MAX_SLICE. */
function slice(state: GameState, dt: number, rng: Rng): GameState {
  const shots = state.shots
    .map((s) => ({ ...s, x: s.x + s.vx * dt, y: s.y + s.vy * dt }))
    .filter((s) => s.x >= -HIT_RADIUS && s.x <= WIDTH + HIT_RADIUS && s.y >= -HIT_RADIUS)
  let missiles = state.missiles.map((m) => ({ ...m, x: m.x + m.vx * dt, y: m.y + m.vy * dt }))

  // One shot takes out one missile, and the first it reaches.
  const spent = new Set<number>()
  const downed = new Set<number>()
  for (const s of shots) {
    const hit = missiles.find(
      (m) => !downed.has(m.id) && (m.x - s.x) ** 2 + (m.y - s.y) ** 2 <= HIT_RADIUS ** 2,
    )
    if (hit) {
      spent.add(s.id)
      downed.add(hit.id)
    }
  }
  missiles = missiles.filter((m) => !downed.has(m.id))
  const destroyed = state.destroyed + downed.size

  let next: GameState = {
    ...state,
    shots: shots.filter((s) => !spent.has(s.id)),
    missiles,
    destroyed,
    sinceSpawn: state.sinceSpawn + dt,
    sinceShot: state.sinceShot + dt,
  }

  // The twentieth hit wins even if another missile lands in the same slice:
  // the player did what the round asked of them first.
  if (destroyed >= TARGET) return { ...next, status: 'won' }

  const landed = missiles.find((m) => m.y >= GROUND_Y)
  if (landed) return { ...next, status: 'lost', impactX: landed.x }

  const d = difficulty(destroyed)
  if (next.sinceSpawn >= d.spawnEvery && missiles.length < d.maxAirborne) {
    next = launch(next, rng)
  }
  return next
}

/**
 * Advance the round by `dt` seconds. Does nothing unless a round is on, so the
 * page can call it every frame regardless of what is on screen.
 */
export function step(state: GameState, dt: number, rng: Rng): GameState {
  if (state.status !== 'playing') return state
  let remaining = Math.min(Math.max(0, dt), MAX_FRAME)
  let next = state
  while (remaining > 0 && next.status === 'playing') {
    const d = Math.min(remaining, MAX_SLICE)
    next = slice(next, d, rng)
    remaining -= d
  }
  return next
}
